import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface Profile {
  id: string;
  user_id: string;
  display_name: string | null;
  subscription_tier: string;
  instagram_handle: string | null;
  facebook_handle: string | null;
  twitter_handle: string | null;
  linkedin_handle: string | null;
  profile_completed_prompts: number;
  phone: string | null;
  phone_verified: boolean | null;
  onboarding_completed_at: string | null;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  isPremium: boolean;
  signUp: (email: string, password: string, displayName?: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId: string, userMeta?: Record<string, any>) => {
    const betterName =
      userMeta?.full_name || userMeta?.name || userMeta?.display_name || userMeta?.user_name || null;

    let { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle();

    if (!data) {
      const { data: inserted } = await supabase
        .from("profiles")
        .insert({ user_id: userId, display_name: betterName })
        .select("*")
        .maybeSingle();

      data = inserted ?? null;
    }

    if (data && betterName) {
      const name = data.display_name || "";
      const looksLikePlaceholder = !name || name.includes("@") || /^[a-z0-9._-]+$/i.test(name);

      if (looksLikePlaceholder && betterName !== name) {
        await supabase
          .from("profiles")
          .update({ display_name: betterName })
          .eq("user_id", userId);
        setProfile({ ...data, display_name: betterName });
        return;
      }
    }

    setProfile(data);
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        setTimeout(() => void fetchProfile(session.user.id, session.user.user_metadata), 0);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) void fetchProfile(session.user.id, session.user.user_metadata);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, displayName?: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: { display_name: displayName || email.split("@")[0] },
      },
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const refreshProfile = async () => {
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    if (currentUser) await fetchProfile(currentUser.id, currentUser.user_metadata);
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
  };

  const tier = profile?.subscription_tier;
  const [subPremium, setSubPremium] = useState(false);

  useEffect(() => {
    if (!user) { setSubPremium(false); return; }
    let active = true;
    const check = async () => {
      const { data } = await supabase
        .from("subscriptions")
        .select("status, current_period_end")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!active) return;
      const s = data?.status;
      const notExpired = !data?.current_period_end || new Date(data.current_period_end) > new Date();
      const ok = !!s && ["active", "authenticated", "trialing", "past_due"].includes(s) && notExpired;
      const gracefulCancel = !!s && (s === "canceled" || s === "cancelled") && notExpired;
      setSubPremium(ok || gracefulCancel);
    };
    void check();
    const ch = supabase
      .channel(`auth-sub-${user.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "subscriptions", filter: `user_id=eq.${user.id}` }, () => { void check(); })
      .subscribe();
    return () => { active = false; void supabase.removeChannel(ch); };
  }, [user]);

  const profilePremium = tier === "paid" || tier === "premium" || tier === "circle" || tier === "advisory_board";
  const isPremium = subPremium || profilePremium;

  return (
    <AuthContext.Provider value={{ session, user, profile, loading, isPremium, signUp, signIn, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
